/* eslint-disable @typescript-eslint/no-redeclare */
import React, { useState } from 'react';
import { Box, Typography, Badge } from '@mui/material';

interface DateSelectorProps {
  onChange?: (date: Date) => void;
  jobCounts?: Record<string, number>;
  appliedDates?: Record<string, number>;
}

const DateSelector: React.FC<DateSelectorProps> = ({ onChange, jobCounts = {}, appliedDates = {} }) => {
  type dateData = {
    fullDate: Date;
    month: number;
    date: number;
    day: string;
    label: string | null;
    isToday: boolean;
    textColor: string | null;
    showMonthLabel: boolean;
    monthLabelText: string;
  }
  const [selectedIndex, setSelectedIndex] = useState(0);

  const weekdays = ["日", "月", "火", "水", "木", "金", "土"];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayYMD = today.toISOString().split("T")[0];

  const dateData: dateData[] = [];

  for (let i = 0; i < 31; i++) {
    const W_date = new Date();
    W_date.setHours(0, 0, 0, 0);
    W_date.setDate(today.getDate() + i);

    const month = W_date.getMonth();
    const date = W_date.getDate();
    const weekdayIndex = W_date.getDay();
    const day = weekdays[weekdayIndex];

    const ymd = W_date.toISOString().split("T")[0];
    const isToday = ymd === todayYMD;

    // 今日・明日・明後日のラベル
    let label: string | null = null;
    if (i === 0) label = '今日';
    else if (i === 1) label = '明日';
    else if (i === 2) label = '明後日';

    const textColor: string | null = null;

    // 月ラベルは月が変わる境目のみ表示（初日には出さない）
    const showMonthLabel = i > 0 && date === 1;
    const monthLabelText = `${W_date.getMonth() + 1}月`;

    dateData.push({
      fullDate: new Date(W_date),
      month,
      date,
      day,
      label,
      isToday,
      textColor,
      showMonthLabel,
      monthLabelText
    });
  }

  const handleDateClick = (index: number) => {
    setSelectedIndex(index);
    if (onChange) {
      onChange(new Date(dateData[index].fullDate));
    }
  };

  return (
    <Box sx={{
      display: 'flex',
      gap: 0,
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
      px: 1,
      width: '100%',
      maxWidth: '100vw'
    }}>
      {dateData.map((item, index) => {
        const isSelected = index === selectedIndex;
        const d = item.fullDate;
        const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const count = jobCounts[dateKey] || 0;
        const appliedCount = appliedDates[dateKey] || 0;

        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flexShrink: 0,
              // 7等分: (100vw - 左右px16) / 7
              width: 'calc((100vw - 16px) / 7)',
              minWidth: 0,
            }}
          >
            {/* 月のラベル */}
            <Box sx={{ height: '18px', display: 'flex', alignItems: 'center', mb: 0.25 }}>
              {item.showMonthLabel && (
                <Typography
                  sx={{
                    fontSize: '0.6rem',
                    fontWeight: 'bold',
                    color: '#888',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item.monthLabelText}
                </Typography>
              )}
            </Box>

            {/* 日付カード（右上に募集中件数バッジ） */}
            <Badge
              badgeContent={count}
              color="error"
              overlap="rectangular"
              invisible={count === 0}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.6rem',
                  height: '16px',
                  minWidth: '16px',
                  padding: '0 4px',
                  top: 4,
                  right: 4,
                },
              }}
            >
              <Box
                onClick={() => handleDateClick(index)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 'calc((100vw - 16px) / 7 - 8px)',
                  aspectRatio: '1 / 1.35',
                  borderRadius: '12px',
                  bgcolor: isSelected ? '#3c3c3c' : '#fff',
                  border: isSelected ? 'none' : '1px solid #e7e7e7',
                  color: isSelected ? 'white' : (item.textColor || '#3c3c3c'),
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'background-color 0.2s',
                  userSelect: 'none',
                  touchAction: 'manipulation',
                  '&:hover': {
                    bgcolor: isSelected ? '#3c3c3c' : '#f5f5f5'
                  }
                }}
              >
                {/* ラベル（今日・明日・明後日） */}
                {item.label ? (
                  <Typography
                    sx={{
                      fontSize: '0.55rem',
                      fontWeight: 'bold',
                      lineHeight: 1,
                      color: isSelected ? 'rgba(255,255,255,0.85)' : '#888',
                    }}
                  >
                    {item.label}
                  </Typography>
                ) : (
                  <Box sx={{ height: '0.55rem' }} />
                )}

                {/* 日付数字 */}
                <Typography
                  sx={{
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                    lineHeight: 1.3,
                  }}
                >
                  {item.date}
                </Typography>

                {/* 曜日 */}
                <Typography
                  sx={{
                    fontSize: '0.65rem',
                    lineHeight: 1,
                    color: isSelected ? 'rgba(255,255,255,0.7)' : (item.textColor || '#888'),
                  }}
                >
                  {item.day}
                </Typography>
              </Box>
            </Badge>

            {/* 日付ボックス下のタグ表示エリア（出勤予定のみ） */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '20px', mt: 0.3 }}>
              {appliedCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.6rem',
                    bgcolor: '#e8f5e9',
                    color: '#2e7d32',
                    px: 0.5,
                    py: 0.2,
                    borderRadius: 0.5,
                    fontWeight: 'bold',
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                  }}
                >
                  出勤予定
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default DateSelector;
